/**
 * PreviewBanner — a fixed banner shown while preview mode is active.
 *
 * Announces that unreleased features are visible and offers an
 * "Exit preview" action that clears the persisted preview flag + drops
 * the `?preview` param (via `usePreview().exitPreview`).
 *
 * Renders nothing when not previewing, so it's safe to mount
 * unconditionally inside the shell.
 *
 * Tokenized: token color utilities + `text-*` roles + Tailwind's 4px
 * scale only. No inline styles, no arbitrary `text-[Npx]`, radius by tier.
 */
import { Eye, X } from "lucide-react";
import { usePreview } from "./PreviewProvider";

export function PreviewBanner() {
  const { previewing, exitPreview } = usePreview();
  if (!previewing) return null;

  return (
    <div
      data-testid="preview-banner"
      aria-live="polite"
      className="flex items-center justify-center gap-3 px-4 py-2 bg-warning-muted border-b border-warning-strong text-warning text-caption"
    >
      <Eye className="w-4 h-4 shrink-0" aria-hidden="true" />
      <span>Preview mode — showing unreleased features</span>
      <button
        type="button"
        data-testid="preview-exit"
        onClick={exitPreview}
        className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 rounded-pill border border-warning-strong bg-transparent text-warning text-micro font-semibold cursor-pointer outline-none transition-colors hover:bg-warning-muted hover:border-warning focus-visible:ring-2 focus-visible:ring-accent-muted"
      >
        <X className="w-3 h-3 shrink-0" aria-hidden="true" />
        Exit preview
      </button>
    </div>
  );
}
